import { useMemo } from 'react';
import { CURRENCIES } from '@/utils/format';
import { useCurrency } from '@/hooks/useCurrency';

/**
 * Currency-aware input that formats numbers with thousand separators as you type.
 * For CLP: 1500000 → 1.500.000
 * For USD: 1500000 → 1,500,000.00
 *
 * Props:
 * - value: raw numeric string (e.g. "1500000")
 * - onChange: receives raw numeric string
 * - currencyOverride: optional currency code (defaults to user's currency)
 * - All other props forwarded to <input>
 */
export default function CurrencyInput({ value, onChange, currencyOverride, className = '', ...props }) {
  const { currency: userCurrency } = useCurrency();
  const currency = currencyOverride || userCurrency;
  const config = CURRENCIES.find((c) => c.code === currency);
  const decimals = config?.decimals ?? 0;
  const locale = config?.locale || 'es-CL';

  const formatDisplay = (numStr) => {
    if (!numStr && numStr !== '0') return '';
    const num = Number(numStr);
    if (isNaN(num)) return numStr;
    return new Intl.NumberFormat(locale, {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  const handleChange = (e) => {
    const input = e.target.value;

    if (decimals === 0) {
      // CLP: only digits allowed
      const digits = input.replace(/\D/g, '');
      onChange(digits);
    } else {
      // USD/EUR/MXN: digits + one decimal point
      let cleaned = input.replace(/[^\d.,]/g, '');
      // Determine thousand vs decimal separator based on locale
      const useCommaDec = locale.startsWith('es'); // es-* uses comma as decimal
      if (useCommaDec) {
        // Period is thousand separator → strip it, comma is decimal → convert to period
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        // Comma is thousand separator → strip it, period is decimal
        cleaned = cleaned.replace(/,/g, '');
      }
      // Now cleaned has only digits and at most one period
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts.slice(0, -1).join('') + '.' + parts[parts.length - 1];
      } else if (parts.length === 2) {
        cleaned = parts[0] + '.' + parts[1].slice(0, decimals);
      }
      // Remove leading zeros except "0." or plain "0"
      cleaned = cleaned.replace(/^0+(?=\d)/, '');
      onChange(cleaned);
    }
  };

  const displayValue = useMemo(() => {
    if (!value && value !== '0') return '';
    // If user is typing a decimal (ends with "."), preserve it
    if (decimals > 0 && String(value).endsWith('.')) {
      return formatDisplay(value.slice(0, -1)) + (locale.startsWith('es') ? ',' : '.');
    }
    // If trailing decimal zeros (e.g., "100.50"), format but ensure decimals show
    if (decimals > 0 && String(value).includes('.')) {
      const [int, dec] = String(value).split('.');
      const formatted = formatDisplay(int);
      const sep = locale.startsWith('es') ? ',' : '.';
      return formatted + sep + dec;
    }
    return formatDisplay(value);
  }, [value, decimals, locale]);

  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      className={className}
      {...props}
    />
  );
}
